import { useEffect, useRef } from 'react';

// A handler returns false when it has nothing to do, so the key keeps its browser default.
export type Keymap = Record<string, (e: KeyboardEvent) => void | false>;

const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * Global keyboard shortcuts. Keys: 'j', 'Escape', 'meta+k', 'shift+G'…
 * Suppressed while focus is in a text field, select or contenteditable, except Escape
 * (so Esc always closes/blurs). Checkboxes don't suppress them, so pass enabled=false
 * while a dialog owns the keyboard, and always for a dialog with checkboxes.
 */
export default function useKeymap(map: Keymap, enabled = true): void {
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // A focused checkbox takes no text, so shortcuts still work after clicking one.
      const typing = !!target && ((TYPING_TAGS.has(target.tagName) && (target as HTMLInputElement).type !== 'checkbox') || target.isContentEditable);
      if (typing && e.key !== 'Escape') return;
      const combo = `${e.metaKey || e.ctrlKey ? 'meta+' : ''}${e.shiftKey && e.key.length > 1 ? 'shift+' : ''}${e.key.length === 1 ? e.key.toLowerCase() : e.key}`;
      const handler = mapRef.current[combo];
      if (handler && handler(e) !== false) e.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
